import React, { useState, useEffect } from 'react';
import { FileEdit, CreditCard, ShieldCheck, CheckCircle2, XCircle, Phone, Mail, Globe, MessageSquare, RotateCw, ExternalLink } from 'lucide-react';
import PdfReplacer from './components/PdfReplacer';
import PaymentGenerator from './components/PaymentGenerator';
import FileRotator from './components/FileRotator';

enum Tab {
  PDF_REPLACER = 'pdf',
  PAYMENT_GEN = 'payment',
  FILE_ROTATOR = 'rotator',
  SUCCESS = 'success',
  CANCEL = 'cancel'
}

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>(Tab.PDF_REPLACER);

  // Handle redirects from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status === 'success') {
      setActiveTab(Tab.SUCCESS);
    } else if (status === 'cancel') {
      setActiveTab(Tab.CANCEL);
    }
  }, []);

  const ContactSection = () => (
    <div className="mt-10 pt-8 border-t border-zinc-200 text-left">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare size={16} className="text-[#D4AF37]" />
        <p className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Customer Support</p>
      </div>
      <div className="space-y-4">
        <a href="tel:+442037288884" className="flex items-center gap-3 text-zinc-600 hover:text-zinc-900 transition-colors group">
          <div className="bg-zinc-100 p-2.5 rounded-lg group-hover:bg-[#D4AF37]/20 transition-colors">
            <Phone size={16} className="text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Call Us</p>
            <span className="text-sm font-semibold text-zinc-900">+4420 3728 8884</span>
          </div>
        </a>
        <a href="mailto:info@goldfinchexecutive.co.uk" className="flex items-center gap-3 text-zinc-600 hover:text-zinc-900 transition-colors group">
          <div className="bg-zinc-100 p-2.5 rounded-lg group-hover:bg-[#D4AF37]/20 transition-colors">
            <Mail size={16} className="text-[#D4AF37]" />
          </div>
          <div>
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Email Us</p>
            <span className="text-sm font-semibold text-zinc-900">info@goldfinchexecutive.co.uk</span>
          </div>
        </a>
      </div>
    </div>
  );

  if (activeTab === Tab.SUCCESS) {
    return (
      <div className="min-h-screen bg-white text-zinc-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl p-10 text-center shadow-xl animate-in zoom-in-95 duration-300">
          <div className="flex justify-center mb-6">
            <div className="bg-green-500/10 p-4 rounded-full">
              <CheckCircle2 className="text-green-500 w-16 h-16" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-4 text-zinc-900">Payment Successful</h1>
          <p className="text-zinc-600 mb-8 leading-relaxed">
            Thank you for your payment to <span className="text-[#D4AF37] font-semibold text-lg">Goldfinch Executive</span>. Your transaction has been processed securely. A confirmation receipt has been sent to your email.
          </p>
          
          <div className="space-y-4">
            <a 
              href="https://goldfinchexecutive.co.uk/"
              className="w-full bg-[#D4AF37] text-black font-bold py-4 rounded-lg hover:bg-[#C19A2E] active:scale-[0.98] transition-all shadow-[0_0_20px_rgba(212,175,55,0.2)] flex items-center justify-center gap-2"
            >
              <Globe size={18} />
              Return to Official Website
            </a>
          </div>

          <ContactSection />
        </div>
      </div>
    );
  }

  if (activeTab === Tab.CANCEL) {
    return (
      <div className="min-h-screen bg-white text-zinc-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-2xl p-10 text-center shadow-xl animate-in zoom-in-95 duration-300">
          <div className="flex justify-center mb-6">
            <div className="bg-red-500/10 p-4 rounded-full">
              <XCircle className="text-red-500 w-16 h-16" />
            </div>
          </div>
          <h1 className="text-3xl font-bold mb-4 text-zinc-900">Payment Cancelled</h1>
          <p className="text-zinc-600 mb-8 leading-relaxed">
            The transaction was not completed. If you encountered an error or wish to use an alternative payment method, please contact our team.
          </p>
          
          <div className="space-y-4">
            <p className="text-zinc-500 text-sm italic">Please refer to your original invoice or email to re-initiate the payment.</p>
            <a 
              href="https://goldfinchexecutive.co.uk/"
              className="w-full bg-zinc-100 text-zinc-900 border border-zinc-200 font-bold py-4 rounded-lg hover:bg-zinc-200 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Globe size={18} />
              Visit Website
            </a>
          </div>

          <ContactSection />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900 flex flex-col">
      {/* Background Glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#D4AF37]/5 blur-[120px] rounded-full" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#D4AF37]/5 blur-[120px] rounded-full" />
      </div>

      {/* Header */}
      <header className="border-b border-zinc-200 py-6 px-8 sticky top-0 bg-white/80 backdrop-blur-md z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-[#D4AF37] w-8 h-8" />
            <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
              Goldfinch <span className="text-[#D4AF37]">Executive</span>
            </h1>
          </div>
          
          <a 
            href="https://mygoldfinch.co.uk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all font-bold text-sm"
          >
            CRM
            <ExternalLink size={14} />
          </a>
        </div>
      </header>

      {/* Navigation */}
      <nav className="border-b border-zinc-200 bg-zinc-50 overflow-x-auto no-scrollbar relative z-10">
        <div className="max-w-5xl mx-auto flex whitespace-nowrap">
          <button
            onClick={() => setActiveTab(Tab.PDF_REPLACER)}
            className={`flex items-center gap-2 py-4 px-6 font-medium transition-all relative group ${
              activeTab === Tab.PDF_REPLACER ? 'text-[#D4AF37]' : 'text-zinc-500 hover:text-zinc-900'
            }`}
          >
            <FileEdit size={18} />
            PDF Link Replacer
            {activeTab === Tab.PDF_REPLACER ? (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37] shadow-[0_0_10px_#D4AF37]" />
            ) : (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent group-hover:bg-zinc-200 transition-colors" />
            )}
          </button>
          <button
            onClick={() => setActiveTab(Tab.PAYMENT_GEN)}
            className={`flex items-center gap-2 py-4 px-6 font-medium transition-all relative group ${
              activeTab === Tab.PAYMENT_GEN ? 'text-[#D4AF37]' : 'text-zinc-500 hover:text-zinc-900'
            }`}
          >
            <CreditCard size={18} />
            Payment Generator
            {activeTab === Tab.PAYMENT_GEN ? (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37] shadow-[0_0_10px_#D4AF37]" />
            ) : (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent group-hover:bg-zinc-200 transition-colors" />
            )}
          </button>
          <button
            onClick={() => setActiveTab(Tab.FILE_ROTATOR)}
            className={`flex items-center gap-2 py-4 px-6 font-medium transition-all relative group ${
              activeTab === Tab.FILE_ROTATOR ? 'text-[#D4AF37]' : 'text-zinc-500 hover:text-zinc-900'
            }`}
          >
            <RotateCw size={18} />
            File Rotator
            {activeTab === Tab.FILE_ROTATOR ? (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37] shadow-[0_0_10px_#D4AF37]" />
            ) : (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-transparent group-hover:bg-zinc-200 transition-colors" />
            )}
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 max-w-5xl w-full mx-auto p-8 relative z-10">
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
          {activeTab === Tab.PDF_REPLACER && <PdfReplacer />}
          {activeTab === Tab.PAYMENT_GEN && <PaymentGenerator />}
          {activeTab === Tab.FILE_ROTATOR && <FileRotator />}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-200 py-10 text-center relative z-10">
        <div className="max-w-5xl mx-auto px-8 flex flex-col items-center gap-3">
          <p className="text-zinc-400 text-[10px] uppercase tracking-[0.2em] font-medium">
            &copy; {new Date().getFullYear()} Goldfinch Executive. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;